import { useId, useMemo, useState, type KeyboardEvent } from 'react';
import { Input } from '@vd/ui';
import styles from './JiraProjectCombobox.module.css';

export type JiraProjectOption = { id: string; key: string; name: string };

/** Most matches drawn at once; typing narrows the rest. */
export const MAX_SHOWN = 100;

const label = (project: JiraProjectOption) => `${project.key}: ${project.name}`;

/**
 * The projects matching `query`, best first: a key that starts with it, then
 * a key or name that contains it, each kept in Jira's own order. An empty
 * query matches everything.
 */
export function matchProjects(projects: JiraProjectOption[], query: string): JiraProjectOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return projects;
  const keyFirst: JiraProjectOption[] = [];
  const rest: JiraProjectOption[] = [];
  for (const project of projects) {
    const key = project.key.toLowerCase();
    if (key.startsWith(needle)) keyFirst.push(project);
    else if (key.includes(needle) || project.name.toLowerCase().includes(needle)) rest.push(project);
  }
  return [...keyFirst, ...rest];
}

/**
 * A Jira project picker the owner can type into, for accounts with more
 * projects than a plain select can show usefully (Jason, 13 Oct). It follows
 * the ARIA combobox pattern: the text box filters by key or name, the arrow
 * keys move through the matches, Enter or a click chooses, and Escape or
 * leaving the box puts back the project already chosen. `id` lands on the
 * text box, so a FormField's label names it.
 */
export function JiraProjectCombobox({ id, projects, value, onChange, disabled }: {
  id?: string;
  projects: JiraProjectOption[];
  value: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
}) {
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const chosen = projects.find((project) => project.id === value);
  // While the person is typing, the box shows their words; otherwise the chosen project.
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => matchProjects(projects, query ?? ''), [projects, query]);
  const shown = matches.slice(0, MAX_SHOWN);
  // Only what changes what the person can reach is announced: a count on
  // every keystroke was noise beside the options the reader already speaks
  // (z.ai review round 2).
  const status = shown.length === 0 ? 'No projects match.'
    : matches.length > shown.length ? `Showing ${shown.length} of ${matches.length}. Keep typing to narrow it.` : '';

  const close = () => { setOpen(false); setQuery(null); };
  const choose = (project: JiraProjectOption) => {
    onChange(project.id);
    close();
  };
  const moveTo = (index: number) => {
    setActive(index);
    document.getElementById(optionId(index))?.scrollIntoView?.({ block: 'nearest' });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Keys pressed while an input method composes a word (Japanese, Chinese)
    // belong to it: Enter there confirms the word, not a project (Codex review
    // round 1). 229 is how some browsers report such a key.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      if (shown.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      moveTo((active + step + shown.length) % shown.length);
    } else if (event.key === 'Enter') {
      if (open && shown[active]) {
        event.preventDefault();
        choose(shown[active]);
      }
    } else if (event.key === 'Escape') {
      if (open) {
        // This Escape closes the list and nothing else, such as a dialog around it.
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
  };

  return (
    <div className={styles.combo}>
      <Input
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && shown[active] ? optionId(active) : undefined}
        autoComplete="off"
        placeholder={projects.length > 0 ? 'Type to find a project' : 'No projects'}
        value={query ?? (chosen ? label(chosen) : '')}
        disabled={disabled}
        onFocus={(event) => { event.currentTarget.select(); setOpen(true); setActive(0); }}
        onClick={(event) => {
          // Reopened by a click with the focus already here: select the words, as focus does, so typing replaces them.
          if (!open) event.currentTarget.select();
          setOpen(true);
        }}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); setActive(0); }}
        onKeyDown={onKeyDown}
        onBlur={close}
      />
      {/* What the list says about itself, for a screen reader (z.ai review round 1). */}
      <span role="status" className={styles.srOnly}>{open && !disabled ? status : ''}</span>
      {open && !disabled && (
        // A press anywhere on the list, its scrollbar included, keeps the focus
        // in the box, so the list stays open (z.ai review round 2).
        <ul id={listId} role="listbox" className={styles.list} onMouseDown={(event) => event.preventDefault()}>
          {shown.map((project, index) => (
            <li
              key={project.id}
              id={optionId(index)}
              role="option"
              aria-selected={project.id === value}
              className={index === active ? `${styles.option} ${styles.active}` : styles.option}
              // The box keeps the focus through a press on an option, so the
              // list stays open until the click; only a primary click, which is
              // what fires click, chooses (Codex review round 1).
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(project)}
              onMouseEnter={() => setActive(index)}
            >
              <span className={styles.key}>{project.key}</span>
              <span className={styles.name}>{project.name}</span>
            </li>
          ))}
          {/* Seen here, heard once through the status above. */}
          {shown.length === 0 && <li className={styles.note} role="presentation" aria-hidden="true">No projects match.</li>}
          {matches.length > shown.length && (
            <li className={styles.note} role="presentation" aria-hidden="true">
              Showing {shown.length} of {matches.length}. Keep typing to narrow it.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
